import type {ReactElement} from 'react';
import React from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ContextualMenuDropdownOption, ContextualMenuDropdownType} from '@Components/contextual-menu/components/contextual-menu-dropdown-option';
import {setAiSubtitlesGenerationLanguage, setHasLanguageSelectorJustClosed} from '@Panels/ai-subtitle-panel/ai-subtitle-reducer';
import {AiTranscriptSupportedLanguage} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import type {
  AiSubtitleGenerationLanguageSelectorProps,
  LanguageDropdownItem,
} from '@Panels/ai-subtitle-panel/components/ai-subtitle-generation-language-selector/ai-subtitle-generation-language-selector.types';
import {DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {CustomListItem} from '@Components/contextual-menu/components/custom-list-item';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './ai-subtitle-generation-language-selector.module.scss';

export function AiSubtitleGenerationLanguageSelector({
  selectorClasses = styles.selectorWidth,
  isDisabled = false,
  isMobile = false,
  dropdownSelectorType = ContextualMenuDropdownType.DEFAULT,
  showLanguageIcon = true,
}: AiSubtitleGenerationLanguageSelectorProps): ReactElement<any> {
  const dispatch = useAppDispatch();

  const aiSubtitlePanelReduxState = useAppSelector((state) => {
    return state.aiSubtitle.aiSubtitlesPanel;
  });

  const getLanguagesDropdownItems = (): LanguageDropdownItem[] => {
    return [
      {
        id: AiTranscriptSupportedLanguage.AFRIKAANS,
        text: window.i18next.t('pmwjs_language_afrikaans'),
      },
      {
        id: AiTranscriptSupportedLanguage.ARABIC,
        text: window.i18next.t('pmwjs_language_arabic'),
      },
      {
        id: AiTranscriptSupportedLanguage.ARMENIAN,
        text: window.i18next.t('pmwjs_language_armenian'),
      },
      {
        id: AiTranscriptSupportedLanguage.AZERBAIJANI,
        text: window.i18next.t('pmwjs_language_azerbaijani'),
      },
      {
        id: AiTranscriptSupportedLanguage.BELARUSIAN,
        text: window.i18next.t('pmwjs_language_belarusian'),
      },
      {
        id: AiTranscriptSupportedLanguage.BOSNIAN,
        text: window.i18next.t('pmwjs_language_bosnian'),
      },
      {
        id: AiTranscriptSupportedLanguage.BULGARIAN,
        text: window.i18next.t('pmwjs_language_bulgarian'),
      },
      {
        id: AiTranscriptSupportedLanguage.CATALAN,
        text: window.i18next.t('pmwjs_language_catalan'),
      },
      {
        id: AiTranscriptSupportedLanguage.CHINESE,
        text: window.i18next.t('pmwjs_language_chinese'),
      },
      {
        id: AiTranscriptSupportedLanguage.CROATIAN,
        text: window.i18next.t('pmwjs_language_croatian'),
      },
      {
        id: AiTranscriptSupportedLanguage.CZECH,
        text: window.i18next.t('pmwjs_language_czech'),
      },
      {
        id: AiTranscriptSupportedLanguage.DANISH,
        text: window.i18next.t('pmwjs_language_danish'),
      },
      {
        id: AiTranscriptSupportedLanguage.DUTCH,
        text: window.i18next.t('pmwjs_language_dutch'),
      },
      {
        id: AiTranscriptSupportedLanguage.ENGLISH,
        text: window.i18next.t('pmwjs_language_english'),
      },
      {
        id: AiTranscriptSupportedLanguage.ESTONIAN,
        text: window.i18next.t('pmwjs_language_estonian'),
      },
      {
        id: AiTranscriptSupportedLanguage.FINNISH,
        text: window.i18next.t('pmwjs_language_finnish'),
      },
      {
        id: AiTranscriptSupportedLanguage.FRENCH,
        text: window.i18next.t('pmwjs_language_french'),
      },
      {
        id: AiTranscriptSupportedLanguage.GALICIAN,
        text: window.i18next.t('pmwjs_language_galician'),
      },
      {
        id: AiTranscriptSupportedLanguage.GERMAN,
        text: window.i18next.t('pmwjs_language_german'),
      },
      {
        id: AiTranscriptSupportedLanguage.GREEK,
        text: window.i18next.t('pmwjs_language_greek'),
      },
      {
        id: AiTranscriptSupportedLanguage.HEBREW,
        text: window.i18next.t('pmwjs_language_hebrew'),
      },
      {
        id: AiTranscriptSupportedLanguage.HINDI,
        text: window.i18next.t('pmwjs_language_hindi'),
      },
      {
        id: AiTranscriptSupportedLanguage.HUNGARIAN,
        text: window.i18next.t('pmwjs_language_hungarian'),
      },
      {
        id: AiTranscriptSupportedLanguage.ICELANDIC,
        text: window.i18next.t('pmwjs_language_icelandic'),
      },
      {
        id: AiTranscriptSupportedLanguage.INDONESIAN,
        text: window.i18next.t('pmwjs_language_indonesian'),
      },
      {
        id: AiTranscriptSupportedLanguage.ITALIAN,
        text: window.i18next.t('pmwjs_language_italian'),
      },
      {
        id: AiTranscriptSupportedLanguage.JAPANESE,
        text: window.i18next.t('pmwjs_language_japanese'),
      },
      {
        id: AiTranscriptSupportedLanguage.KANNADA,
        text: window.i18next.t('pmwjs_language_kannada'),
      },
      {
        id: AiTranscriptSupportedLanguage.KAZAKH,
        text: window.i18next.t('pmwjs_language_kazakh'),
      },
      {
        id: AiTranscriptSupportedLanguage.KOREAN,
        text: window.i18next.t('pmwjs_language_korean'),
      },
      {
        id: AiTranscriptSupportedLanguage.LATVIAN,
        text: window.i18next.t('pmwjs_language_latvian'),
      },
      {
        id: AiTranscriptSupportedLanguage.LITHUANIAN,
        text: window.i18next.t('pmwjs_language_lithuanian'),
      },
      {
        id: AiTranscriptSupportedLanguage.MACEDONIAN,
        text: window.i18next.t('pmwjs_language_macedonian'),
      },
      {
        id: AiTranscriptSupportedLanguage.MALAY,
        text: window.i18next.t('pmwjs_language_malay'),
      },
      {
        id: AiTranscriptSupportedLanguage.MARATHI,
        text: window.i18next.t('pmwjs_language_marathi'),
      },
      {
        id: AiTranscriptSupportedLanguage.MAORI,
        text: window.i18next.t('pmwjs_language_maori'),
      },
      {
        id: AiTranscriptSupportedLanguage.NEPALI,
        text: window.i18next.t('pmwjs_language_nepali'),
      },
      {
        id: AiTranscriptSupportedLanguage.NORWEGIAN,
        text: window.i18next.t('pmwjs_language_norwegian'),
      },
      {
        id: AiTranscriptSupportedLanguage.PERSIAN,
        text: window.i18next.t('pmwjs_language_persian'),
      },
      {
        id: AiTranscriptSupportedLanguage.POLISH,
        text: window.i18next.t('pmwjs_language_polish'),
      },
      {
        id: AiTranscriptSupportedLanguage.PORTUGUESE,
        text: window.i18next.t('pmwjs_language_portuguese'),
      },
      {
        id: AiTranscriptSupportedLanguage.ROMANIAN,
        text: window.i18next.t('pmwjs_language_romanian'),
      },
      {
        id: AiTranscriptSupportedLanguage.RUSSIAN,
        text: window.i18next.t('pmwjs_language_russian'),
      },
      {
        id: AiTranscriptSupportedLanguage.SPANISH,
        text: window.i18next.t('pmwjs_language_spanish'),
      },
      {
        id: AiTranscriptSupportedLanguage.SERBIAN,
        text: window.i18next.t('pmwjs_language_serbian'),
      },
      {
        id: AiTranscriptSupportedLanguage.SLOVAK,
        text: window.i18next.t('pmwjs_language_slovak'),
      },
      {
        id: AiTranscriptSupportedLanguage.SLOVENIAN,
        text: window.i18next.t('pmwjs_language_slovenian'),
      },
      {
        id: AiTranscriptSupportedLanguage.SWAHILI,
        text: window.i18next.t('pmwjs_language_swahili'),
      },
      {
        id: AiTranscriptSupportedLanguage.SWEDISH,
        text: window.i18next.t('pmwjs_language_swedish'),
      },
      {
        id: AiTranscriptSupportedLanguage.TAGALOG,
        text: window.i18next.t('pmwjs_language_tagalog'),
      },
      {
        id: AiTranscriptSupportedLanguage.TAMIL,
        text: window.i18next.t('pmwjs_language_tamil'),
      },
      {
        id: AiTranscriptSupportedLanguage.THAI,
        text: window.i18next.t('pmwjs_language_thai'),
      },
      {
        id: AiTranscriptSupportedLanguage.TURKISH,
        text: window.i18next.t('pmwjs_language_turkish'),
      },
      {
        id: AiTranscriptSupportedLanguage.UKRAINIAN,
        text: window.i18next.t('pmwjs_language_ukrainian'),
      },
      {
        id: AiTranscriptSupportedLanguage.URDU,
        text: window.i18next.t('pmwjs_language_urdu'),
      },
      {
        id: AiTranscriptSupportedLanguage.VIETNAMESE,
        text: window.i18next.t('pmwjs_language_vietnamese'),
      },
      {
        id: AiTranscriptSupportedLanguage.WELSH,
        text: window.i18next.t('pmwjs_language_welsh'),
      },
    ];
  };

  const onChangeLanguageForGeneration = (lang: AiTranscriptSupportedLanguage): void => {
    dispatch(setAiSubtitlesGenerationLanguage(lang));

    dispatch(setHasLanguageSelectorJustClosed(true));

    dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
  };

  const getCustomPopup = (): ReactElement<any> => {
    return (
      <ul className={`${isMobile ? '_full-width' : ''} ${styles.languagesPopup}`}>
        {getLanguagesDropdownItems().map((languageDropdownItem) => {
          return (
            <CustomListItem
              key={languageDropdownItem.id}
              doScrollIntoView
              id={languageDropdownItem.id}
              onClick={(): void => {
                if (languageDropdownItem.id === aiSubtitlePanelReduxState.inputLanguage) {
                  return;
                }

                onChangeLanguageForGeneration(languageDropdownItem.id);
              }}
              isSelected={languageDropdownItem.id === aiSubtitlePanelReduxState.inputLanguage}
              text={languageDropdownItem.text}
            />
          );
        })}
      </ul>
    );
  };

  return (
    <div className="_full-width flex-center">
      {showLanguageIcon ? <Icon className="spacing-m-r-2" size={IconSize.SIZE_ICON_16} icon="icon-translation" /> : null}
      <div className={styles.languageContextualOption}>
        <ContextualMenuDropdownOption
          customPopup={getCustomPopup()}
          selectorClasses={selectorClasses}
          isForSubOption
          popupMaxHeight={250}
          popupWidth={150}
          popUpHasCustomWidth={!isMobile}
          position={DROPDOWN_POSITION.FIXED}
          isDisabled={isDisabled}
          onClick={onChangeLanguageForGeneration}
          dropdownItems={getLanguagesDropdownItems()}
          selectedItemId={aiSubtitlePanelReduxState.inputLanguage}
          selectorType={dropdownSelectorType}
          id="ai-subtitles-language-selector"
          label={window.i18next.t('pmwjs_language')}
          modalTitle={window.i18next.t('pmwjs_language')}
        />
      </div>
    </div>
  );
}
